import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Inbox, RefreshCw, Phone, Mail, MapPin, Package, MessageSquare, CalendarClock } from "lucide-react";
import { API } from "../data/site";

const STATUS_OPTIONS = ["new", "contacted", "quoted", "closed"];

const STATUS_STYLES = {
  new: "bg-safety-tint text-safety-dark border-safety/30",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  quoted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

const StatusPill = ({ value }) => (
  <span className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] ${STATUS_STYLES[value] || STATUS_STYLES.closed}`}>
    {value}
  </span>
);

const AdminEnquiries = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-enquiries", filter],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/enquiries${filter ? `?status=${filter}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load enquiries");
      return res.json();
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await fetch(`${API}/admin/enquiries/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-enquiries"] }),
  });

  const stats = data?.stats || { total: 0, today: 0, new: 0 };
  const enquiries = data?.enquiries || [];

  return (
    <div data-testid="admin-enquiries-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-navy">Enquiries</h1>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Every website RFQ lands here + your Gmail
          </p>
        </div>
        <button
          onClick={() => refetch()}
          data-testid="admin-enquiries-refresh-button"
          className="flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-safety hover:text-safety"
        >
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        {[
          { icon: Inbox, label: "Total", value: stats.total, id: "stat-total" },
          { icon: CalendarClock, label: "Today", value: stats.today, id: "stat-today" },
          { icon: MessageSquare, label: "New", value: stats.new, id: "stat-new" },
        ].map((s) => (
          <div key={s.label} className="border border-slate-200 bg-white p-5" data-testid={s.id}>
            <s.icon size={18} className="text-safety" />
            <p className="mt-2 font-display text-2xl font-extrabold text-navy">{s.value}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["", ...STATUS_OPTIONS].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`admin-filter-${s || "all"}`}
            className={`border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${
              filter === s ? "border-safety bg-safety text-white" : "border-slate-300 bg-white text-slate-600 hover:border-navy"
            }`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Loading enquiries…</p>
      ) : enquiries.length === 0 ? (
        <div className="mt-10 border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="admin-enquiries-empty">
          <Inbox size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 font-display text-lg font-bold text-navy">No enquiries here yet.</p>
          <p className="mt-1 text-sm text-slate-500">New website enquiries appear instantly in this list and your Gmail.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
          {enquiries.map((e) => (
            <div key={e.id} className="border border-slate-200 bg-white p-5" data-testid={`admin-enquiry-card-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <p className="font-display text-base font-bold text-navy">{e.name}</p>
                    {e.company && <span className="font-mono text-[10px] text-slate-500">· {e.company}</span>}
                    <StatusPill value={e.status} />
                  </div>
                  <p className="mt-1 font-mono text-[10px] text-slate-500">
                    {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} IST
                  </p>
                </div>
                <select
                  value={e.status || "new"}
                  onChange={(ev) => updateStatus.mutate({ id: e.id, status: ev.target.value })}
                  data-testid={`enquiry-status-select-${e.id}`}
                  className="border border-slate-300 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-navy focus:border-safety focus:outline-none"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="mt-4 grid gap-3 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
                <span className="flex items-center gap-2"><Mail size={13} className="shrink-0 text-safety" /><a href={`mailto:${e.email}`} className="break-all hover:text-safety">{e.email}</a></span>
                <span className="flex items-center gap-2"><Phone size={13} className="shrink-0 text-safety" /><a href={`tel:${e.phone}`} className="hover:text-safety">{e.phone}</a></span>
                {e.city_state && <span className="flex items-center gap-2"><MapPin size={13} className="shrink-0 text-safety" />{e.city_state}</span>}
                {e.quantity && <span className="flex items-center gap-2"><Package size={13} className="shrink-0 text-safety" />{e.quantity}</span>}
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="bg-safety-tint px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-safety-dark">{e.product_of_interest}</span>
                {e.designation && <span className="bg-slate-100 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-600">{e.designation}</span>}
              </div>

              {e.message && (
                <p className="mt-3 border-l-2 border-slate-200 pl-3.5 text-sm leading-relaxed text-slate-600">{e.message}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEnquiries;
